/**
 * TextBlock - Combines heading and paragraph content in a simple block
 * Replaces complex ContentBlock usage with an easy-to-edit component
 */

import React from 'react'
import { Box } from '@mui/material'
import Heading from '../elements/Heading'
import Paragraph from '../elements/Paragraph'

interface TextBlockProps {
	title?: string
	titleLevel?: 1 | 2 | 3 | 4 | 5 | 6
	titleColor?: 'primary' | 'secondary' | 'white' | 'inherit'
	content: string
	contentSize?: 'small' | 'medium' | 'large'
	contentColor?: 'primary' | 'secondary' | 'white' | 'inherit'
	align?: 'left' | 'center' | 'right'
	spacing?: 'tight' | 'normal' | 'loose'
	maxWidth?: string | { xs?: string; sm?: string; md?: string; lg?: string; xl?: string }
}

const TextBlock: React.FC<TextBlockProps> = ({
	title,
	titleLevel = 2,
	titleColor = 'inherit',
	content,
	contentSize = 'medium',
	contentColor = 'inherit',
	align = 'left',
	spacing = 'normal',
	maxWidth
}) => {
	const getSpacing = () => {
		switch (spacing) {
			case 'tight':
				return 1
			case 'normal':
				return 2
			case 'loose':
				return 4
			default:
				return 2
		}
	}

	return (
		<Box
			sx={{
				textAlign: align,
				maxWidth: maxWidth,
				mx: align === 'center' ? 'auto' : undefined
			}}
		>
			{title && (
				<Heading level={titleLevel} color={titleColor} align={align} weight="bold">
					{title}
				</Heading>
			)}

			<Box sx={{ mt: title ? getSpacing() : 0 }}>
				<Paragraph size={contentSize} color={contentColor} align={align}>
					{content}
				</Paragraph>
			</Box>
		</Box>
	)
}

export default TextBlock
